import React, { useRef, useState, useEffect } from "react";
import { motion, useMotionValue, useSpring, useMotionTemplate } from "framer-motion";

/**
 * CursorSpotlight — adds a soft amber radial glow that follows the cursor
 * inside the wrapped section. Best used on dark backgrounds.
 */
export const CursorSpotlight = ({
  children,
  color = "rgba(245, 158, 11, 0.18)",
  size = 520,
  className = "",
}) => {
  const ref = useRef(null);
  const [hovering, setHovering] = useState(false);
  const x = useMotionValue(-9999);
  const y = useMotionValue(-9999);
  const sx = useSpring(x, { stiffness: 120, damping: 18, mass: 0.6 });
  const sy = useSpring(y, { stiffness: 120, damping: 18, mass: 0.6 });
  const bg = useMotionTemplate`radial-gradient(${size}px circle at ${sx}px ${sy}px, ${color}, transparent 60%)`;

  useEffect(() => {
    const node = ref.current;
    if (!node) return;
    const onMove = (e) => {
      const rect = node.getBoundingClientRect();
      x.set(e.clientX - rect.left);
      y.set(e.clientY - rect.top);
    };
    const onEnter = () => setHovering(true);
    const onLeave = () => setHovering(false);
    node.addEventListener("mousemove", onMove);
    node.addEventListener("mouseenter", onEnter);
    node.addEventListener("mouseleave", onLeave);
    return () => {
      node.removeEventListener("mousemove", onMove);
      node.removeEventListener("mouseenter", onEnter);
      node.removeEventListener("mouseleave", onLeave);
    };
  }, [x, y]);

  return (
    <div ref={ref} className={`relative ${className}`}>
      <motion.div
        aria-hidden
        className="pointer-events-none absolute inset-0 z-0 transition-opacity duration-500"
        style={{ opacity: hovering ? 1 : 0, background: bg }}
      />
      <div className="relative z-[1]">{children}</div>
    </div>
  );
};

export default CursorSpotlight;
